import type { ComponentProps, FC, ReactNode } from 'react'

import Link from 'next/link'
import { LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'soft' | 'danger' | 'danger-outline' | 'link'

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm'

const VARIANTS: Record<ButtonVariant, string> = {
    primary: 'bg-primary text-primary-foreground shadow-sm shadow-primary/25 hover:bg-primary/90 dark:shadow-none',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/75',
    outline: 'border border-input bg-card text-foreground shadow-card hover:border-primary/35 hover:bg-accent hover:text-accent-foreground dark:bg-input/20',
    ghost: 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
    soft: 'bg-primary/10 text-primary hover:bg-primary/15',
    danger: 'bg-destructive text-destructive-foreground shadow-sm shadow-destructive/20 hover:bg-destructive/90 dark:shadow-none',
    'danger-outline': 'border border-destructive/30 bg-transparent text-destructive hover:border-destructive/50 hover:bg-destructive/10',
    link: 'h-auto px-0 text-primary underline-offset-4 hover:underline'
}

const SIZES: Record<ButtonSize, string> = {
    xs: 'h-7 gap-1 rounded-md px-2.5 text-xs',
    sm: 'h-8 gap-1.5 rounded-md px-3 text-[13px]',
    md: 'h-10 gap-2 rounded-lg px-4 text-sm',
    lg: 'h-11 gap-2 rounded-lg px-5 text-[15px]',
    icon: 'size-10 rounded-lg',
    'icon-sm': 'size-8 rounded-md'
}

type VariantOptions = {
    variant?: ButtonVariant
    size?: ButtonSize
    className?: string
}

export const buttonVariants = ({ variant = 'primary', size = 'md', className }: VariantOptions = {}): string => {
    return cn(
        'inline-flex shrink-0 items-center justify-center font-semibold whitespace-nowrap outline-none select-none',
        'transition-[color,background-color,border-color,box-shadow,opacity,transform] active:translate-y-px',
        'focus-visible:ring-3 focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-50',
        '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4',
        VARIANTS[variant],
        variant === 'link' ? 'text-sm' : SIZES[size],
        className
    )
}

type ButtonProps = ComponentProps<'button'> & {
    variant?: ButtonVariant
    size?: ButtonSize
    loading?: boolean
}

export const Button: FC<ButtonProps> = ({
    variant,
    size,
    loading = false,
    className,
    children,
    disabled,
    type = 'button',
    ...props
}): ReactNode => {
    return (
        <button
            type={type}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            className={buttonVariants({ variant, size, className })}
            {...props}
        >
            {loading && <LoaderCircle className='animate-spin' aria-hidden='true' />}
            {children}
        </button>
    )
}

type ButtonLinkProps = ComponentProps<typeof Link> & {
    variant?: ButtonVariant
    size?: ButtonSize
}

export const ButtonLink: FC<ButtonLinkProps> = ({ variant, size, className, ...props }): ReactNode => {
    return <Link className={buttonVariants({ variant, size, className })} {...props} />
}
